/**
 * Como é o atendimento: com IA (a imagem nasce da foto) ou só a fotografia, que sai como foi tirada, pronta para
 * imprimir (#95). Escolhido no começo; muda as etapas que existem.
 */
export const MODOS_DO_ATENDIMENTO = ["ia", "fotografia"] as const;
export type ModoDoAtendimento = (typeof MODOS_DO_ATENDIMENTO)[number];

/**
 * O nome de cada modo, o mesmo no começo do atendimento e no filtro de tipo da Biblioteca (#99): a foto salva "Só
 * fotografia" é encontrada pelo nome com que foi feita.
 */
export const ROTULO_DO_MODO: Record<ModoDoAtendimento, string> = {
  ia: "Com IA",
  fotografia: "Só fotografia",
};

/**
 * O que ainda impede sair da primeira etapa, ou `null`.
 *
 * O Local vem antes do nome no "Só fotografia" (#101): é a exigência que o operador não espera, e o botão a diz assim
 * que o modo é escolhido. `institutionId` é o Local que existe na lista — um que saiu dela não conta.
 */
export function oQueFaltaEmQuem(
  modo: ModoDoAtendimento,
  quem: { nome: string; institutionId: string | null },
): "local" | "nome" | null {
  if (modo === "fotografia" && !quem.institutionId) return "local";
  if (quem.nome.trim().length < 2) return "nome";
  return null;
}

/** O atendimento com IA, do começo ao fim. */
export const ETAPAS = ["quem", "foto", "auxiliares", "prompt", "gerar", "capa", "entrega"] as const;

/**
 * Só a fotografia: sem referências, pedido, geração nem capa. Entre a foto e a entrega há um passo de salvar,
 * porque é ali que a foto vira publicada — e onde o operador decide se ela vai à galeria do salão.
 */
export const ETAPAS_DA_FOTOGRAFIA = ["quem", "foto", "salvar", "entrega"] as const;

export type Etapa = (typeof ETAPAS)[number] | (typeof ETAPAS_DA_FOTOGRAFIA)[number];

interface DefEtapa {
  titulo: string;
  /** Etapas opcionais podem ser puladas sem consequência. */
  opcional?: boolean;
}

export const DEF_ETAPAS: Record<Etapa, DefEtapa> = {
  quem: { titulo: "Quem é" },
  foto: { titulo: "A foto" },
  auxiliares: { titulo: "Referências", opcional: true },
  prompt: { titulo: "O pedido" },
  gerar: { titulo: "Gerar" },
  capa: { titulo: "Capa", opcional: true },
  salvar: { titulo: "Salvar" },
  entrega: { titulo: "Entregar" },
};

export function etapasDoModo(modo: ModoDoAtendimento): readonly Etapa[] {
  return modo === "fotografia" ? ETAPAS_DA_FOTOGRAFIA : ETAPAS;
}

export function proxima(etapa: Etapa, modo: ModoDoAtendimento = "ia"): Etapa | null {
  const etapas = etapasDoModo(modo);
  const i = etapas.indexOf(etapa);
  return i < 0 ? null : (etapas[i + 1] ?? null);
}

export function anterior(etapa: Etapa, modo: ModoDoAtendimento = "ia"): Etapa | null {
  const etapas = etapasDoModo(modo);
  const i = etapas.indexOf(etapa);
  return i > 0 ? (etapas[i - 1] ?? null) : null;
}
